/**
 * Analyst Co-Pilot turn model + the SSE-event reducer.
 *
 * Kept in a sibling `.ts` (not the page `.tsx`) so it is pure + unit-testable and
 * so the page module exports only its component (the react-refresh ESLint rule).
 *
 * The Analyst renders the SAME block-by-block trace as the Agent Console — its
 * thinking, its tool calls, then its answer — by folding each streamed
 * `ConsoleEvent` into an `AssistantBlock[]` with the shared agentConsole helpers.
 * Unlike the Quality / Tech Support co-pilots this door is TEXT-ONLY (no
 * `voice_reply`), so a turn carries no spoken rundown.
 */
import {
  liftAgentFileCards,
  mergeAgentFiles,
  type AgentFileData,
} from '../components/agentConsole/agentFileCards';
import { detailToTurns } from '../components/agentConsole/detailToTurns';
import type { MessageAttachment } from '../components/chat/messageAttachments';
import {
  appendLiveText,
  appendLiveThinking,
  applyLiveToolResult,
  toolCallViewFromUse,
  type AssistantBlock,
  type ConsoleEvent,
  type ConversationDetail,
} from '../services/agentConsole';

export type Turn =
  | {
      id: string;
      role: 'user';
      text: string;
      // When the message happened — persisted created_at on resume, client
      // send time live. Shown on the bubble (browser-local convention).
      createdAt?: string | null;
      attachments?: MessageAttachment[];
    }
  | {
      id: string;
      role: 'assistant';
      blocks: AssistantBlock[]; // the thinking / tool / answer trace
      streaming: boolean;
      createdAt?: string | null;
      /** Files the agent attached this turn (attach_file) — one card each. */
      agentFiles?: AgentFileData[];
      /**
       * A server-side recovery note streamed ahead of the answer (the thread's
       * history no longer fit the model, so this reply started from a fresh
       * context). A calm note above the answer, never an error.
       */
      notice?: string | null;
    };

export type AssistantTurnModel = Extract<Turn, { role: 'assistant' }>;

/**
 * Fold one streamed `ConsoleEvent` into an assistant turn, returning ONLY the
 * changed fields (pure). The stream-lifecycle frames (state / done / error /
 * cancelled) are handled by the page, not here.
 */
export function reduceTurn(
  turn: AssistantTurnModel,
  evt: ConsoleEvent,
): Partial<AssistantTurnModel> {
  const blocks = turn.blocks;
  switch (evt.type) {
    case 'text':
      return { blocks: appendLiveText(blocks, evt.text) };
    case 'thinking':
      return { blocks: appendLiveThinking(blocks, evt.text) };
    case 'tool_use':
      return {
        blocks: [
          ...blocks,
          { kind: 'tool', tool: toolCallViewFromUse(evt, `live-${blocks.length}`) },
        ],
      };
    case 'tool_result': {
      const applied = applyLiveToolResult(
        blocks,
        evt.tool_use_id,
        evt.is_error,
        evt.summary,
      );
      // A successful attach_file frame becomes a file card the moment its ack
      // lands — lifted out of the trace (pending/errored chips stay).
      const { blocks: kept, files } = liftAgentFileCards(applied);
      return {
        blocks: kept,
        ...(files.length
          ? { agentFiles: mergeAgentFiles(turn.agentFiles, files) }
          : {}),
      };
    }
    case 'notice':
      return { notice: evt.message };
    default:
      return {};
  }
}

/**
 * Rebuild a SAVED conversation into the Analyst's turn model so a resumed thread
 * renders identically to one just streamed. The shared `detailToTurns` does the
 * walk (block reconstruction); this door carries no spoken rundown, so the
 * mapping onto its `Turn` is direct.
 */
export function detailToAnalystTurns(detail: ConversationDetail): Turn[] {
  return detailToTurns(detail).map<Turn>((t) =>
    t.role === 'user'
      ? {
          id: t.id,
          role: 'user',
          text: t.text ?? '',
          createdAt: t.createdAt ?? null,
          attachments: t.attachments,
        }
      : {
          id: t.id,
          role: 'assistant',
          blocks: t.blocks ?? [],
          streaming: false,
          createdAt: t.createdAt ?? null,
          // Agent-file cards come pre-lifted from the shared detail walk.
          agentFiles: t.agentFiles,
          // A persisted recovery notice (fresh context) rides onto this turn.
          notice: t.notice ?? null,
        },
  );
}
